"use client"

import { useState, useEffect } from "react";

export const scheduleMacrotask = (action: () => void) => {
  setTimeout(() => {
    action();
  }, 0);
};

export const useManagerEntity = (): typeof window.manager => {
  const [ManagerEntity, setManagerEntity] = useState<
    typeof window.manager | null
  >(null);
  useEffect(() => {
    const handleManagerInit = () => {
      if (window.manager) {
        console.log('[MANAGER] Finished init...')
        setManagerEntity(window.manager);
      }
    };

    // Check if manager is already initialized (e.g., navigating back to Practice)
    if (window.manager) {
      console.log('[MANAGER] Already initialized, setting immediately...')
      setManagerEntity(window.manager);
    }

    document.addEventListener('managerInited', handleManagerInit);

    return () => {
      document.removeEventListener('managerInited', handleManagerInit);
    };
  }, []);

  return ManagerEntity as typeof window.manager;
};

export type ManagerEffectFunctionType = React.EffectCallback;

export const useManagerEffect = (
  action: ManagerEffectFunctionType,
  deps: any[]
) => {
  const managerEntity = useManagerEntity();
  useEffect(() => {
    let cleanup;
    if (managerEntity) {
      cleanup = action();
    }
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managerEntity, ...deps]);
};

export const useManagerEntityState = <T,>(
  key: string
): [T | null, (newVal: T) => void] => {
  const ManagerEntity = useManagerEntity();
  const [state, setState] = useState<T | null>(
    ManagerEntity?.[`${key}`] ?? null
  );

  const onSetState = (newVal: T) => {
    setState(newVal);
    scheduleMacrotask(() => {
      ManagerEntity[`${key}`] = newVal;
    });
  };

  useEffect(() => {
    const value = ManagerEntity?.[`${key}`];
    if (value) {
      setState(value);
    }
  }, [ManagerEntity, key]);

  return [state, onSetState];
};